import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from '../ui/button'
import { useConsent } from '../../context/ConsentContext'

interface Props {
  open: boolean
  onClose: () => void
}

export default function CookiePreferencesDialog({ open, onClose }: Props) {
  const { t } = useTranslation()
  const { categories, saveConsent } = useConsent()
  const [analytics, setAnalytics] = useState(categories.analytics)
  const [marketing, setMarketing] = useState(categories.marketing)

  if (!open) return null

  async function handleSave() {
    await saveConsent({ analytics, marketing })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
          aria-label={t('cookie.close')}
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-base font-semibold text-gcg-text mb-1">{t('cookie.preferencesTitle')}</h2>
        <p className="text-sm text-neutral-500 mb-5">{t('cookie.preferencesSubtitle')}</p>

        <div className="flex flex-col gap-4">
          {/* Functional — always on */}
          <label className="flex items-start gap-3 cursor-not-allowed opacity-70">
            <input
              type="checkbox"
              checked
              disabled
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary accent-primary"
            />
            <div>
              <span className="text-sm font-medium text-gcg-text">{t('cookie.functional')}</span>
              <p className="text-xs text-neutral-500 mt-0.5">{t('cookie.functionalDesc')}</p>
            </div>
          </label>

          {/* Analytics */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={analytics}
              onChange={e => setAnalytics(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 accent-primary"
            />
            <div>
              <span className="text-sm font-medium text-gcg-text">{t('cookie.analytics')}</span>
              <p className="text-xs text-neutral-500 mt-0.5">{t('cookie.analyticsDesc')}</p>
            </div>
          </label>

          {/* Marketing */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={e => setMarketing(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 accent-primary"
            />
            <div>
              <span className="text-sm font-medium text-gcg-text">{t('cookie.marketing')}</span>
              <p className="text-xs text-neutral-500 mt-0.5">{t('cookie.marketingDesc')}</p>
            </div>
          </label>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('cookie.cancel')}
          </Button>
          <Button size="sm" onClick={handleSave}>
            {t('cookie.savePreferences')}
          </Button>
        </div>
      </div>
    </div>
  )
}
